// Building blocks shared by every step of the migration wizard: the stepper,
// the two-column step layout with its guidance panel, and the small pieces the
// preview and report repeat.
import type { ReactNode } from "react"
import { Card } from "../ui"
import { MIGRATION_STEPS, boldRuns } from "../../lib/migrations"

/** The six steps across the top. Done steps can be revisited only while the
 *  run still allows it, so the caller decides which ones are reachable. */
export function Stepper({
  current,
  reachable = () => false,
  onSelect,
  steps = MIGRATION_STEPS,
}: {
  current: number
  reachable?: (step: number) => boolean
  onSelect?: (step: number) => void
  /** The batch migration has its own six. */
  steps?: readonly string[]
}) {
  return (
    <nav aria-label="Migration steps" className="flex gap-1 overflow-x-auto rounded-lg border border-border bg-surface p-3">
      {steps.map((label, i) => {
        const n = i + 1
        const done = n < current
        const isCurrent = n === current
        const canGo = !isCurrent && reachable(n)
        return (
          <button
            key={label}
            type="button"
            disabled={!canGo}
            aria-current={isCurrent ? "step" : undefined}
            onClick={() => canGo && onSelect?.(n)}
            className={`flex min-w-10 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors sm:min-w-28 ${
              isCurrent ? "bg-surface-muted font-medium text-foreground" : "text-muted"
            } ${canGo ? "hover:bg-surface-muted hover:text-foreground" : "cursor-default"}`}
          >
            <span
              className={`flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                isCurrent
                  ? "bg-accent text-accent-foreground"
                  : done
                    ? "bg-accent-soft text-accent"
                    : "border border-border bg-surface-muted text-faint"
              }`}
            >
              {done ? "✓" : n}
            </span>
            <span className={isCurrent ? "inline" : "hidden sm:inline"}>{label}</span>
          </button>
        )
      })}
    </nav>
  )
}

/** One step: its card on the left, what-this-step-does on the right. */
export function StepLayout({
  title,
  intro,
  guide,
  footer,
  children,
}: {
  title: string
  intro: ReactNode
  guide: { title?: string; points: ReactNode[]; tip?: ReactNode }
  footer?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_17rem]">
      <Card className="flex min-w-0 flex-col gap-5 p-5">
        <div>
          <h2 className="text-base font-semibold text-foreground">{title}</h2>
          <p className="mt-1 max-w-prose text-sm text-muted">{intro}</p>
        </div>
        {children}
        {footer && <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">{footer}</div>}
      </Card>
      <aside className="flex flex-col gap-3 rounded-lg border border-border bg-surface-subtle p-4 lg:sticky lg:top-4">
        <h3 className="text-sm font-semibold text-foreground">{guide.title ?? "What this step does"}</h3>
        <ul className="flex list-disc flex-col gap-1.5 pl-4 text-xs text-muted">
          {guide.points.map((point, i) => (
            <li key={i}>{point}</li>
          ))}
        </ul>
        {guide.tip && <p className="border-t border-border pt-3 text-xs text-muted">{guide.tip}</p>}
      </aside>
    </div>
  )
}

/** Text with **bold** runs, as the source's token help is written. */
export function Rich({ text }: { text: string }) {
  return (
    <>
      {boldRuns(text).map((run, i) =>
        run.bold ? (
          <b key={i} className="font-semibold text-foreground">
            {run.text}
          </b>
        ) : (
          <span key={i}>{run.text}</span>
        ),
      )}
    </>
  )
}

export function Note({ tone, children }: { tone: "success" | "warning" | "danger" | "neutral"; children: ReactNode }) {
  const tones = {
    success: "border-success/30 bg-success-soft text-success",
    warning: "border-warning/30 bg-warning-soft text-warning",
    danger: "border-danger/30 bg-danger-soft text-danger",
    neutral: "border-border bg-surface-muted text-muted",
  }[tone]
  return (
    <div role={tone === "danger" ? "alert" : undefined} className={`rounded-md border px-3 py-2 text-sm ${tones}`}>
      {children}
    </div>
  )
}

export function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card className="p-4">
      <p className="text-xs text-muted">{label}</p>
      <p className="mt-0.5 font-mono text-xl font-semibold tabular-nums text-foreground">{value}</p>
      {sub && <p className="text-xs text-faint">{sub}</p>}
    </Card>
  )
}

/** The thicker bar from the Settings storage card. */
export function Bar({ percent, tone = "success" }: { percent: number; tone?: "success" | "warning" | "danger" }) {
  const fill = { success: "bg-success", warning: "bg-warning", danger: "bg-danger" }[tone]
  return (
    <div
      className="h-2 overflow-hidden rounded-full border border-border bg-surface-muted"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(percent)}
    >
      <div className={`h-full ${fill} transition-all motion-reduce:transition-none`} style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
    </div>
  )
}

/** A short list of "what — detail" lines: warnings, files left out. */
export function ItemList({ items }: { items: { what: string; detail: ReactNode }[] }) {
  return (
    <ul className="flex flex-col gap-1 text-xs">
      {items.map((item, i) => (
        <li key={i} className="flex gap-2">
          <span className="min-w-24 shrink-0 text-faint">{item.what}</span>
          <span className="min-w-0 text-foreground [overflow-wrap:anywhere]">{item.detail}</span>
        </li>
      ))}
    </ul>
  )
}
